/**
 * NotesPanel — the canonical notes feed surface for CR-SEQ-2026-013 v3.
 *
 * Refactor of the existing develop-branch SubscriberNotes pattern (a chronological
 * <ul> feed with an inline composer below it) against the polymorphic backend, plus
 * the FRD additions (reportable flag, archive lifecycle, snapshot Quote with full
 * ancestry cascading, keyword + date filters on consolidated views, pagination).
 *
 * Two modes (distinguished by props):
 *   - Consolidated: scoped to a subscriber or a scheme; shows keyword + date filters.
 *     Posts attach to the panel scope (Subscribers / WRSHierarchies).
 *   - Per-artifact: scoped to a single artifact; no filters. Posts attach to that artifact.
 *
 * Visible chrome conditional on permission:
 *   - composer visible only when user has CreateNotes
 *   - archive/unarchive buttons visible only when user has ArchiveNotes
 *   - Show-archived toggle visible to anyone viewing the panel (ViewNotes).
 *     Transparency: a viewer should be able to see what was archived even
 *     when they cannot archive/unarchive themselves — like a library book
 *     pulled from the open shelf is still requestable. The destructive
 *     archive/unarchive actions remain gated by ArchiveNotes.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format, formatDistance } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import classNames from "classnames";
import {
  ArchiveBoxIcon,
  ChatBubbleLeftEllipsisIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { useAppContext } from "@context/AppContext";
import Card from "@components/layout/Card";
import Pagination from "@components/layout/Pagination";
import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";
import TextArea from "@components/form/TextArea";
import SearchInput from "@components/form/SearchInput";
import CheckboxInputWithLabel from "@components/form/CheckboxInputWithLabel";
import SelectFilter from "@components/form/SelectFilter";
import ENV from "@config/env";
import {
  addNote,
  archiveNote,
  dateInputToEndOfDay,
  dateInputToStartOfDay,
  getNotesPaginated,
  Note,
  setNoteReportable,
  unarchiveNote,
} from "@services/notes";
import { DBTables, NOTEABLE_TABLES } from "@utils/constants/dbTables";

const PAGE_LIMIT = 20;
const QUOTE_PREVIEW_LIMIT = 200;
// Mirrors AdminCreateNoteValidator's per-file size cap (10mb). Frontend guard
// rejects oversize files instantly so the user gets a friendly toast instead
// of waiting for the upload to round-trip and bounce off the server with 413.
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

type CommonProps = {
  /**
   * Display name for the panel's scope, used in the empty-state copy.
   * For consolidated mode: the subscriber's name or the scheme's name.
   * For per-artifact mode: the artifact's title.
   */
  artifactName?: string;
  /**
   * When true, the panel renders without write affordances regardless of the
   * viewer's permissions: composer hidden, archive/unarchive hidden, Quote
   * hidden, reportable toggle replaced by its read-only badge. Used by
   * surfaces that surface notes for inspection only (e.g. report cell modal).
   */
  readOnly?: boolean;
};

type ConsolidatedSubscriberProps = CommonProps & {
  subscriberId: string;
  level1ResourceId?: never;
  referenceTable?: never;
  referenceId?: never;
};

type ConsolidatedSchemeProps = CommonProps & {
  level1ResourceId: string;
  subscriberId?: never;
  referenceTable?: never;
  referenceId?: never;
};

type PerArtifactProps = CommonProps & {
  referenceTable: DBTables;
  referenceId: string;
  subscriberId?: never;
  level1ResourceId?: never;
};

export type NotesPanelProps =
  | ConsolidatedSubscriberProps
  | ConsolidatedSchemeProps
  | PerArtifactProps;

const isConsolidated = (
  props: NotesPanelProps,
): props is ConsolidatedSubscriberProps | ConsolidatedSchemeProps =>
  "subscriberId" in props || "level1ResourceId" in props;

const NotesPanel: React.FunctionComponent<NotesPanelProps> = (
  props,
) => {
  const { t } = useTranslation();
  const { checkPermissions, loggedInInfo } = useAppContext();
  const queryClient = useQueryClient();
  const feedTopRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLFormElement>(null);

  // readOnly forces write affordances off regardless of permission. Surfaces
  // that consume notes for inspection (e.g. report cell modal) pass this so
  // the panel cleanly degrades to a feed view.
  const canCreate = !props.readOnly && checkPermissions(["CreateNotes"]);
  const canArchive = !props.readOnly && checkPermissions(["ArchiveNotes"]);
  const consolidatedMode = isConsolidated(props);

  // Tracks the id (temp + real) of a note that should briefly flash after
  // a successful post, so the user sees their entry land at the top of the
  // newest-first feed.
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());

  const flashHighlight = (id: string) => {
    setHighlightedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    window.setTimeout(() => {
      setHighlightedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 2500);
  };

  // --- Composer scope: where new notes attach -------------------------------
  const composerScope = useMemo<{
    referenceTable: DBTables;
    referenceId: string;
  } | null>(() => {
    if ("subscriberId" in props && props.subscriberId) {
      return {
        referenceTable: DBTables.Subscribers,
        referenceId: props.subscriberId,
      };
    }
    if ("level1ResourceId" in props && props.level1ResourceId) {
      return {
        referenceTable: DBTables.WRSHierarchies,
        referenceId: props.level1ResourceId,
      };
    }
    if ("referenceTable" in props && props.referenceTable && props.referenceId) {
      return {
        referenceTable: props.referenceTable,
        referenceId: props.referenceId,
      };
    }
    return null;
  }, [props]);

  // --- Filters (consolidated mode only) ------------------------------------
  const [keyword, setKeyword] = useState("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);
  // Entity-type filter — both consolidated views (scheme + customer) roll up
  // notes from many entity types, so narrowing by type is useful on each. The
  // keyword search additionally matches the posting user's name, so there is no
  // separate author filter.
  const [entityTypeFilter, setEntityTypeFilter] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [page, setPage] = useState(0); // 0-indexed for the existing Pagination component

  const fetchParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: page + 1, // API uses 1-indexed pages
      pageLimit: PAGE_LIMIT,
      includeArchived: showArchived,
    };
    if ("subscriberId" in props && props.subscriberId) {
      params.subscriberId = props.subscriberId;
    }
    if ("level1ResourceId" in props && props.level1ResourceId) {
      params.level1ResourceId = props.level1ResourceId;
    }
    if ("referenceTable" in props && props.referenceTable) {
      params.referenceTable = props.referenceTable;
      params.referenceId = props.referenceId;
    }
    if (consolidatedMode) {
      if (keyword.trim()) params.keyword = keyword.trim();
      if (fromDate) params.fromDate = dateInputToStartOfDay(fromDate);
      if (toDate) params.toDate = dateInputToEndOfDay(toDate);
      if (entityTypeFilter?.value) params.referenceTable = entityTypeFilter.value;
    }
    return params;
  }, [
    props,
    page,
    showArchived,
    keyword,
    fromDate,
    toDate,
    consolidatedMode,
    entityTypeFilter,
  ]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(0);
  }, [keyword, fromDate, toDate, showArchived, entityTypeFilter]);

  // --- Data fetch -----------------------------------------------------------
  const queryKey = useMemo(
    () => ["notes", "audit-panel", fetchParams],
    [fetchParams],
  );

  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      getNotesPaginated({
        page: page + 1,
        pageLimit: PAGE_LIMIT,
        // readOnly callers (report cell modal) force the same filter the
        // backend used to produce the count on the cell: reportable only,
        // never archived. Local filter state is ignored so the modal can't
        // diverge from the cell.
        includeArchived: props.readOnly ? false : showArchived,
        ...(props.readOnly ? { reportableOnly: true } : {}),
        ...("subscriberId" in props && props.subscriberId
          ? { subscriberId: props.subscriberId }
          : {}),
        ...("level1ResourceId" in props && props.level1ResourceId
          ? { level1ResourceId: props.level1ResourceId }
          : {}),
        ...("referenceTable" in props && props.referenceTable
          ? {
              referenceTable: props.referenceTable,
              referenceId: props.referenceId,
            }
          : {}),
        ...(consolidatedMode && !props.readOnly && keyword.trim()
          ? { keyword: keyword.trim() }
          : {}),
        ...(consolidatedMode && !props.readOnly && fromDate
          ? { fromDate: dateInputToStartOfDay(fromDate) }
          : {}),
        ...(consolidatedMode && !props.readOnly && toDate
          ? { toDate: dateInputToEndOfDay(toDate) }
          : {}),
        ...(consolidatedMode && !props.readOnly && entityTypeFilter?.value
          ? { referenceTable: entityTypeFilter.value as DBTables }
          : {}),
      }),
    { keepPreviousData: true },
  );

  const notes = data?.notes ?? [];
  const lastPage = data?.meta?.lastPage ?? 1;
  const total = data?.meta?.total ?? 0;

  // --- Composer state -------------------------------------------------------
  const [body, setBody] = useState("");
  const [isReportable, setIsReportable] = useState(false);
  const [attachment, setAttachment] = useState<File | undefined>();
  const [quotingNote, setQuotingNote] = useState<Note | null>(null);
  const [quoteExpanded, setQuoteExpanded] = useState(false);

  const resetComposer = () => {
    setBody("");
    setIsReportable(false);
    setAttachment(undefined);
    setQuotingNote(null);
    setQuoteExpanded(false);
  };

  // --- Add note mutation (optimistic insert → invalidate → flash + scroll) -
  // Optimistically prepend the new note to the cached page-1 feed so the user
  // sees their entry land immediately. On error, roll back. On success, scroll
  // the feed to the top and flash-highlight the new row briefly.
  //
  // Mutation variables capture the composer state atomically at mutate() time
  // — without this, resetComposer() in onMutate would null the body before
  // mutationFn reads it, and the server rejects with "note is required".
  type AddNoteVariables = {
    note: string;
    isReportable: boolean;
    attachment?: File;
    parentNoteId?: string;
    quotedSnapshot: Note["quotedSnapshot"];
  };

  const addMutation = useMutation<
    Note,
    unknown,
    AddNoteVariables,
    { previous: unknown; tempId: string }
  >({
    mutationFn: async (vars) => {
      if (!composerScope) {
        throw new Error("NotesPanel: composer scope is unresolved");
      }
      return addNote({
        referenceTable: composerScope.referenceTable,
        referenceId: composerScope.referenceId,
        note: vars.note,
        isReportable: vars.isReportable,
        parentNoteId: vars.parentNoteId,
        attachment: vars.attachment,
      });
    },
    onMutate: async (vars) => {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await queryClient.cancelQueries({ queryKey: ["notes"] });

      const previous = queryClient.getQueryData(queryKey);

      const optimisticNote: Partial<Note> & { id: string } = {
        id: tempId,
        note: vars.note,
        isReportable: vars.isReportable,
        isArchived: false,
        createdAt: new Date().toISOString(),
        postedByUser: {
          id: loggedInInfo?.userDetails?.id ?? "",
          name: loggedInInfo?.userDetails?.name ?? "…",
        },
        quotedSnapshot: vars.quotedSnapshot,
      };

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.notes) return old;
        return {
          ...old,
          notes: [optimisticNote, ...old.notes],
          meta: { ...old.meta, total: (old.meta?.total ?? 0) + 1 },
        };
      });

      flashHighlight(tempId);

      // Safe to reset composer now — variables already captured above.
      resetComposer();
      // Scroll the feed top into view so the new entry is visible.
      window.requestAnimationFrame(() => {
        feedTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });

      return { previous, tempId };
    },
    onSuccess: (created) => {
      toast.success(t("notes.add_note_succeed"), {
        className: "toast toast-success",
      });
      setPage(0);
      // Carry the highlight from the tempId over to the real id so the flash
      // persists across the refetch that replaces optimistic data with real.
      if (created?.id) flashHighlight(created.id);
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      queryClient.invalidateQueries({ queryKey: ["noteCounts"] });
      // Also bust Subscriber/Scheme rollup counts on the listing badges —
      // they aggregate over subscriber_id / level1_resource_id, so a new
      // note posted from this panel affects them too.
      queryClient.invalidateQueries({ queryKey: ["noteRollupCounts"] });
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
      toast.error(t("notes.add_note_error"), {
        className: "toast toast-error",
      });
    },
  });

  // --- Archive / unarchive mutations ----------------------------------------
  const invalidateNoteQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["notes"] });
    queryClient.invalidateQueries({ queryKey: ["noteCounts"] });
    // Also bust Subscriber/Scheme rollup counts on the listing badges — they
    // aggregate over subscriber_id / level1_resource_id, so any change here
    // affects them too.
    queryClient.invalidateQueries({ queryKey: ["noteRollupCounts"] });
  };

  const unarchiveMutation = useMutation<Note, unknown, string>({
    mutationFn: (id) => unarchiveNote(id),
    onSuccess: () => {
      toast.success(t("notes.unarchive_succeed"), {
        className: "toast toast-success",
      });
      invalidateNoteQueries();
    },
    onError: () => {
      toast.error(t("notes.unarchive_error"), {
        className: "toast toast-error",
      });
    },
  });

  const archiveMutation = useMutation<Note, unknown, string>({
    mutationFn: (id) => archiveNote(id),
    onSuccess: (archived) => {
      // Toast with inline Undo — archive is one click and easy to mis-tap on a
      // small text button. Undo restores the note via the unarchive mutation.
      toast.success(
        ({ closeToast }) => (
          <div className="flex items-center justify-between gap-3">
            <span>{t("notes.archive_succeed")}</span>
            {archived?.id && (
              <button
                type="button"
                className="text-sm font-semibold underline"
                onClick={() => {
                  unarchiveMutation.mutate(archived.id);
                  closeToast?.();
                }}
              >
                {t("common.undo")}
              </button>
            )}
          </div>
        ),
        { className: "toast toast-success", autoClose: 5000 },
      );
      invalidateNoteQueries();
    },
    onError: () => {
      toast.error(t("notes.archive_error"), {
        className: "toast toast-error",
      });
    },
  });

  // Reportable toggle (Option B from the UX spec): operators with
  // ArchiveNotes can mark/unmark notes as reportable post-creation. State
  // changes are anchored on Hedera for audit; PDF reports already generated
  // are unaffected — only future report generations see the change.
  const setReportableMutation = useMutation<
    Note,
    unknown,
    { id: string; isReportable: boolean }
  >({
    mutationFn: ({ id, isReportable }) => setNoteReportable(id, isReportable),
    onSuccess: () => {
      toast.success(t("notes.reportable_change_succeed"), {
        className: "toast toast-success",
      });
      invalidateNoteQueries();
    },
    onError: () => {
      toast.error(t("notes.reportable_change_error"), {
        className: "toast toast-error",
      });
    },
  });

  const handleQuote = (note: Note) => {
    setQuotingNote(note);
    setQuoteExpanded(false);
    // Bring composer into view + drop focus into the textarea so the user can
    // start typing immediately. rAF waits for the quote-preview block to mount
    // before measuring scroll position.
    window.requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      composerRef.current?.querySelector("textarea")?.focus();
    });
  };

  const handleClearQuote = () => {
    setQuotingNote(null);
    setQuoteExpanded(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() || addMutation.isLoading) return;
    // Snapshot composer state into mutation variables so resetComposer() in
    // onMutate can't null the body before mutationFn reads it.
    // For optimistic insert we build a client-side snapshot of the quoted
    // note that mirrors what the server will persist on success.
    const optimisticQuotedSnapshot = quotingNote
      ? {
          noteId: quotingNote.id,
          body: quotingNote.note,
          authorName: quotingNote.postedByUser?.name ?? "",
          createdAt: quotingNote.createdAt,
          // Cascade the chain so the optimistic render matches what the
          // server will persist on success.
          quotedSnapshot: quotingNote.quotedSnapshot ?? null,
        }
      : null;
    addMutation.mutate({
      note: body,
      isReportable,
      attachment,
      parentNoteId: quotingNote?.id,
      quotedSnapshot: optimisticQuotedSnapshot,
    });
  };

  // Empty-state copy splits on whether the viewer can post. Users who can
  // create see a friendlier CTA pointing them to the composer below; viewers
  // see a neutral statement.
  const emptyStateCopy = canCreate
    ? props.artifactName
      ? t("notes.empty_for_scope_invite", { artifact: props.artifactName })
      : t("notes.empty_invite")
    : props.artifactName
    ? t("notes.empty_for_scope", { artifact: props.artifactName })
    : t("notes.empty");

  return (
    <Card className="border-none shadow-none rounded-none px-0">
      {/* ---------- Header (filters + show-archived) ----------
          readOnly callers (report cell modal) get no filter chrome: the
          query is pinned to reportable-only / no-archived to match the
          cell count. Hiding the controls prevents users from changing the
          query and getting a result different from what the report shows. */}
      {!props.readOnly && (
        <div className="px-6 py-3 border-b border-gray-200 space-y-3">
          {consolidatedMode && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="notes-filter-keyword">
                  {t("notes.filter_keyword_placeholder")}
                </Label>
                <SearchInput
                  id="notes-filter-keyword"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t("notes.filter_keyword_placeholder") as string}
                />
              </div>
              <div>
                <Label htmlFor="notes-filter-from">
                  {t("notes.filter_date_from")}
                </Label>
                <TextInput
                  id="notes-filter-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="notes-filter-to">
                  {t("notes.filter_date_to")}
                </Label>
                <TextInput
                  id="notes-filter-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              {/* Entity-type filter — both consolidated views (scheme +
                  customer) roll up notes from many entity types. */}
              <div>
                <Label htmlFor="notes-filter-entity-type">
                  {t("notes.filter_entity_type")}
                </Label>
                <SelectFilter
                  id="notes-filter-entity-type"
                  value={entityTypeFilter}
                  onChange={(option) => setEntityTypeFilter(option)}
                  options={NOTEABLE_TABLES.map((table) => ({
                    value: table,
                    label: t(`notes.reference_table.${table}`, {
                      defaultValue: table,
                    }),
                  }))}
                  isClearable
                />
              </div>
            </div>
          )}

          <CheckboxInputWithLabel
            htmlFor="notes-show-archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            label={t("notes.show_archived")}
            inline
          />
        </div>
      )}

      {/* ---------- Body (skeleton / empty / list + pagination) ---------- */}
      {isLoading ? (
        <ul className="divide-y divide-gray-100 animate-pulse">
          {[0, 1, 2].map((i) => (
            <li key={i} className="py-4 px-6">
              <div className="flex space-x-3">
                <div className="rounded-full w-8 h-8 bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 bg-gray-200 rounded" />
                  <div className="h-3 w-3/4 bg-gray-100 rounded" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : notes.length === 0 ? (
        <div className="px-6 py-8 text-sm text-gray-500 text-center">
          {emptyStateCopy}
        </div>
      ) : (
        <>
          <div ref={feedTopRef} aria-hidden="true" />
          <ul className="divide-y divide-gray-200">
            {notes.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                showEntity={consolidatedMode && !props.readOnly}
                canArchive={canArchive}
                canCreate={canCreate}
                canToggleReportable={
                  canArchive ||
                  (canCreate &&
                    note.postedByUserId === loggedInInfo?.userDetails?.id)
                }
                isHighlighted={highlightedIds.has(note.id)}
                onQuote={handleQuote}
                onArchive={() => archiveMutation.mutate(note.id)}
                onUnarchive={() => unarchiveMutation.mutate(note.id)}
                archiveDisabled={archiveMutation.isLoading}
                unarchiveDisabled={unarchiveMutation.isLoading}
                onSetReportable={(isReportable) =>
                  setReportableMutation.mutate({ id: note.id, isReportable })
                }
                setReportableDisabled={setReportableMutation.isLoading}
              />
            ))}
          </ul>
          {lastPage > 1 && (
            <Pagination
              pageNo={page}
              lastPage={lastPage}
              onPageChange={setPage}
              className="py-3 border-t border-gray-200"
            />
          )}
          {total > 0 && (
            <div className="sr-only" aria-live="polite">
              {t("notes.indicator_aria_label", { count: total })}
            </div>
          )}
        </>
      )}

      {/* ---------- Composer ---------- */}
      {canCreate && composerScope && (
        <form
          ref={composerRef}
          onSubmit={handleSubmit}
          className="border-t border-gray-200 px-6 py-4 space-y-2"
        >
          {quotingNote && (
            <div className="border-l-4 border-blue-200 bg-blue-50 px-3 py-2 rounded text-sm space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-600">
                  {t("notes.quoting_attribution", {
                    author: quotingNote.postedByUser?.name ?? "—",
                    relativeTime: formatDistance(
                      new Date(quotingNote.createdAt),
                      new Date(),
                      { addSuffix: true },
                    ),
                  })}
                </span>
                <button
                  type="button"
                  className="text-xs text-blue-700 underline"
                  onClick={handleClearQuote}
                >
                  {t("notes.quote_clear")}
                </button>
              </div>
              {/* Cascade the ancestry chain — preview matches what the new
                  note will look like in the feed after submit. */}
              {quotingNote.quotedSnapshot && (
                <QuotedBlock snapshot={quotingNote.quotedSnapshot} />
              )}
              <p className="text-xs text-gray-700 whitespace-pre-wrap">
                {quoteExpanded ||
                quotingNote.note.length <= QUOTE_PREVIEW_LIMIT
                  ? quotingNote.note
                  : `${quotingNote.note.slice(0, QUOTE_PREVIEW_LIMIT)}…`}
              </p>
              {!quoteExpanded &&
                quotingNote.note.length > QUOTE_PREVIEW_LIMIT && (
                  <button
                    type="button"
                    className="text-xs text-blue-700 underline"
                    onClick={() => setQuoteExpanded(true)}
                  >
                    {t("notes.quote_show_more")}
                  </button>
                )}
            </div>
          )}

          <TextArea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={t("notes.composer_placeholder") as string}
          />

          {/* Footer row: tertiary toolbox actions on the left (icon-paperclip
              + reportable checkbox), primary submit anchored hard right with
              a visual gap. Paperclip uses an icon-button to deliberately read
              quieter than the Post-note primary button, fixing the prior
              "two buttons look identical" issue. */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="flex items-center gap-3 min-w-0">
              {attachment ? (
                <span className="inline-flex items-center gap-1 max-w-xs rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                  <PaperClipIcon className="w-3 h-3 shrink-0" aria-hidden="true" />
                  <span className="truncate" title={attachment.name}>
                    {attachment.name}
                  </span>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setAttachment(undefined)}
                    disabled={addMutation.isLoading}
                    aria-label={t("notes.remove_attachment", {
                      name: attachment.name,
                    }) as string}
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ) : (
                <>
                  <label
                    htmlFor="notes-audit-panel-attachment"
                    className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                    title={t("notes.add_attachment") as string}
                    aria-label={t("notes.add_attachment") as string}
                  >
                    <PaperClipIcon className="w-5 h-5" aria-hidden="true" />
                  </label>
                  <input
                    type="file"
                    id="notes-audit-panel-attachment"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > ATTACHMENT_MAX_BYTES) {
                        toast.error(
                          t("notes.attachment_too_large", { maxSize: "10MB" }),
                          { className: "toast toast-error" },
                        );
                        e.target.value = "";
                        return;
                      }
                      setAttachment(file);
                    }}
                    disabled={addMutation.isLoading}
                  />
                </>
              )}

              <CheckboxInputWithLabel
                htmlFor="notes-audit-panel-reportable"
                checked={isReportable}
                onChange={(e) => setIsReportable(e.target.checked)}
                label={t("notes.reportable_label")}
                labelDescription={t("notes.reportable_badge_tooltip") as string}
                inline
              />
            </div>

            <button
              type="submit"
              className="btn-primary text-sm shrink-0"
              disabled={addMutation.isLoading || !body.trim()}
            >
              {quotingNote
                ? t("notes.composer_submit_quoting")
                : t("notes.composer_submit")}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
};

// --- QuotedBlock --------------------------------------------------------------
//
// Recursive renderer for the quoted-snapshot chain. Each level shows attribution
// + truncated body with its own independent "show more" toggle. Nested levels
// render before this level's body, so the visual order matches a typical email
// quote chain: oldest ancestor at the deepest indent, current quote on top.

type QuotedBlockProps = {
  snapshot: NonNullable<Note["quotedSnapshot"]>;
};

const QuotedBlock: React.FunctionComponent<QuotedBlockProps> = ({ snapshot }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const body = snapshot.body ?? "";
  const showMore = body.length > QUOTE_PREVIEW_LIMIT;
  const displayedBody = expanded ? body : body.slice(0, QUOTE_PREVIEW_LIMIT);

  return (
    <div className="border-l-2 border-gray-300 pl-3 py-1 text-xs text-gray-700 italic space-y-1">
      <span className="block font-medium text-gray-700 not-italic">
        {t("notes.quoting_attribution", {
          author: snapshot.authorName || "—",
          relativeTime: formatDistance(new Date(snapshot.createdAt), new Date(), {
            addSuffix: true,
          }),
        })}
      </span>
      {snapshot.quotedSnapshot && <QuotedBlock snapshot={snapshot.quotedSnapshot} />}
      <p className="whitespace-pre-wrap">
        {displayedBody}
        {!expanded && showMore && "…"}
      </p>
      {!expanded && showMore && (
        <button
          type="button"
          className="text-blue-700 underline not-italic"
          onClick={() => setExpanded(true)}
        >
          {t("notes.quote_show_more")}
        </button>
      )}
    </div>
  );
};

// --- NoteRow ------------------------------------------------------------------

type NoteRowProps = {
  note: Note;
  showEntity: boolean;
  canArchive: boolean;
  canCreate: boolean;
  canToggleReportable: boolean;
  isHighlighted?: boolean;
  onQuote: (note: Note) => void;
  onArchive: () => void;
  onUnarchive: () => void;
  archiveDisabled: boolean;
  unarchiveDisabled: boolean;
  onSetReportable: (isReportable: boolean) => void;
  setReportableDisabled: boolean;
};

const NoteRow: React.FunctionComponent<NoteRowProps> = ({
  note,
  showEntity,
  canArchive,
  canCreate,
  canToggleReportable,
  isHighlighted,
  onQuote,
  onArchive,
  onUnarchive,
  archiveDisabled,
  unarchiveDisabled,
  onSetReportable,
  setReportableDisabled,
}) => {
  const { t } = useTranslation();
  const createdAt = new Date(note.createdAt);

  // Quote button hidden only for archived notes (quoting an archived note is
  // meaningless). The old depth-1 cap is gone: each note carries the full
  // ancestry as cascaded snapshots, so quoting a quote is safe and shows the
  // whole trail.
  const showQuote = canCreate && !note.isArchived;

  // Reportable toggle: ArchiveNotes operators — or the note's own author
  // (mirrors the `setReportableNotes` bouncer rule) — get the interactive
  // checkbox only on ACTIVE notes. Archived notes (and everyone else) fall
  // through to the static badge — toggling reportable on an archived note
  // has no report effect anyway (reports filter reportable AND non-archived).
  const showReportableToggle = canToggleReportable && !note.isArchived;

  // Archived notes are visibly muted so they read as background context when
  // "Show archived" is toggled on, without becoming illegible.
  return (
    <li
      className={classNames(
        "py-4 px-6 transition-colors duration-700",
        isHighlighted && "bg-blue-50",
        note.isArchived && !isHighlighted && "bg-gray-50 opacity-75",
      )}
    >
      <div className="flex space-x-3">
        {/* Avatar: subdued neutral background so it functions as visual anchor
            without competing with the note body. Previously bg-blue-800 +
            font-extrabold dominated the row. */}
        <div className="flex justify-center items-center rounded-full w-8 h-8 bg-gray-200 text-gray-700 text-sm text-center uppercase font-medium shrink-0">
          {note.postedByUser?.name?.[0] ?? "?"}
        </div>

        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex items-center justify-between gap-2">
            {/* Author name: semibold and dark — it's the row's structural
                heading. Prior text-gray-500 failed WCAG AA. */}
            <h3
              className={classNames(
                "text-sm font-semibold truncate",
                note.isArchived ? "text-gray-600" : "text-gray-900",
              )}
            >
              {note.postedByUser?.name}
            </h3>
            <div className="flex items-center gap-2 shrink-0">
              {/* Reportable: ArchiveNotes operators or the note's own author
                  get an interactive checkbox on ACTIVE notes; everyone else
                  (and archived notes) falls through to the static badge — see
                  `showReportableToggle` above. */}
              {showReportableToggle ? (
                <label
                  className="inline-flex items-center gap-1 text-xs text-amber-800 cursor-pointer select-none"
                  title={t("notes.reportable_badge_tooltip") as string}
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500 disabled:opacity-50"
                    checked={note.isReportable}
                    disabled={setReportableDisabled || note.id.startsWith("temp-")}
                    onChange={(e) => onSetReportable(e.target.checked)}
                  />
                  {t("notes.reportable_badge")}
                </label>
              ) : (
                note.isReportable && (
                  <span
                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                    title={t("notes.reportable_badge_tooltip") as string}
                  >
                    {t("notes.reportable_badge")}
                  </span>
                )
              )}
              {note.isArchived && (
                <span className="text-xs text-gray-700 border border-gray-300 rounded px-2 py-0.5 bg-gray-50">
                  {t("notes.archived_badge")}
                </span>
              )}
            </div>
          </div>

          {/* Quoted-snapshot trail — recursive: each ancestor renders its own
              blockquote, nested oldest-deepest, so the full chain travels with
              every quoting note without a JOIN. */}
          {note.quotedSnapshot && <QuotedBlock snapshot={note.quotedSnapshot} />}

          {/* Body: the primary content of the row — slightly heavier weight
              and relaxed leading. Archived rows render with muted color so
              they read as inactive at a glance. */}
          <p
            className={classNames(
              "text-sm leading-relaxed whitespace-pre-wrap",
              note.isArchived ? "text-gray-500" : "text-gray-800",
            )}
          >
            {note.note}
          </p>

          {note.evidences?.[0] && (
            <a
              href={`${ENV.API_HOST}/evidences/${note.evidences[0].id}/download`}
              className="inline-flex items-center gap-1 text-sm text-blue-700 underline"
              target="_blank"
              rel="noreferrer"
            >
              <PaperClipIcon className="w-3.5 h-3.5" aria-hidden="true" />
              {t("notes.attachment_label", {
                file: note.evidences[0].fileName,
              })}
            </a>
          )}

          <div className="flex items-center justify-between gap-2">
            <p
              className="text-xs text-gray-600"
              title={format(createdAt, "PPpp")}
            >
              {showEntity && note.entityLabel
                ? t("notes.posted_on", {
                    entity: `${t(`notes.reference_table.${note.referenceTable}`, {
                      defaultValue: note.referenceTable,
                    })} ${note.entityLabel}`,
                  })
                : t("notes.posted_at")}{" "}
              {formatDistance(createdAt, new Date(), { addSuffix: true })}
            </p>

            {/* Per-note actions: Quote is creative (text+icon, blue, prominent);
                Archive is destructive (icon-only, gray, quieter). Different
                visual weight so the two can't be confused on small targets. */}
            <div className="flex items-center gap-1">
              {showQuote && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-700 hover:bg-blue-50"
                  onClick={() => onQuote(note)}
                  title={t("notes.quote_button_tooltip") as string}
                >
                  <ChatBubbleLeftEllipsisIcon
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                  />
                  {t("notes.quote_button")}
                </button>
              )}
              {canArchive &&
                (note.isArchived ? (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                    onClick={onUnarchive}
                    disabled={unarchiveDisabled}
                    title={t("notes.unarchive") as string}
                    aria-label={t("notes.unarchive") as string}
                  >
                    <ArchiveBoxIcon
                      className="w-4 h-4 rotate-180"
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                    onClick={onArchive}
                    disabled={archiveDisabled}
                    title={t("notes.archive") as string}
                    aria-label={t("notes.archive") as string}
                  >
                    <ArchiveBoxIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default NotesPanel;
